import { dollarsToCents } from '../misc';
import { OFFER_TYPE } from '../../constants/offerType';

// Type guard
export const isNestedChoiceWithQuantity = (
  choice: ChoiceWithQuantity | NestedChoiceWithQuantity,
): choice is NestedChoiceWithQuantity =>
  Array.isArray((choice as NestedChoiceWithQuantity).choiceSets);

// Normalize purchase to quantity 1 for calculation
const asIndividual = (p: PurchaseWithTotals): PurchaseWithTotals => ({
  ...p,
  quantity: 1,
  pointsPrice: p.pointsPrice / p.quantity,
  pointsAward: p.pointsAward / p.quantity,
  moneyPrice: p.moneyPrice / p.quantity,
  moneyDiscount: p.moneyDiscount / p.quantity,
  discountedMoneyPrice: p.discountedMoneyPrice / p.quantity,
});

// Flatten nested choices with quantity
export const getChoicesWithQuantity = (
  choice: NestedChoiceWithQuantity,
): ChoiceWithQuantity[] => {
  return choice?.choiceSets
    .flatMap(set =>
      set.choices.map(c => ({ ...c, freeChoice: Boolean(set.free) })),
    )
    .filter(c => c.quantity);
};

// Recursively checks for target PLUs in purchase or nested choices
const includesTargetPLUs = (
  choices: Array<ChoiceWithQuantity | NestedChoiceWithQuantity>,
  targetPLUs: string[],
  rootPLU?: string,
): boolean => {
  if (rootPLU && targetPLUs.includes(rootPLU)) return true;

  return choices.some(choice => {
    if (targetPLUs.includes(choice.plucode)) return true;
    return isNestedChoiceWithQuantity(choice)
      ? includesTargetPLUs(getChoicesWithQuantity(choice), targetPLUs)
      : false;
  });
};

// Returns the first staged purchase whose top-level PLU or any choice/nested-choice PLU
// matches plucode — the same recursive check used by selectOffer for enforced-PLU coupons.
export const findPurchaseWithPLU = (
  purchases: PurchaseWithTotals[],
  plucode: string,
): PurchaseWithTotals | null => {
  if (!plucode) return null;

  for (const purchase of purchases) {
    if (purchase.plucode === plucode) return purchase;

    for (const choice of purchase.choicesWithQuantity) {
      if (choice.plucode === plucode) return purchase;

      if (isNestedChoiceWithQuantity(choice)) {
        if (getChoicesWithQuantity(choice).some(c => c.plucode === plucode)) {
          return purchase;
        }
      }
    }
  }

  return null;
};

// Helper function to apply discounts recursively to valid choices
const applyDiscountToChoices = (
  choices: Array<ChoiceWithQuantity | NestedChoiceWithQuantity>,
  targetPLUs: string[],
  purchaseId: string,
  // Call back function that calculates discount for each coupon
  calculateFn: (choice: ChoiceWithQuantity) => number,
  discounts: DiscountDelta[],
) => {
  for (const choice of choices) {
    if (!choice?.freeChoice && targetPLUs.includes(choice.plucode)) {
      discounts.push({
        amount: calculateFn(choice),
        purchaseId,
      });
    }

    if (isNestedChoiceWithQuantity(choice)) {
      applyDiscountToChoices(
        getChoicesWithQuantity(choice),
        targetPLUs,
        purchaseId,
        calculateFn,
        discounts,
      );
    }
  }
};

export default function determineDiscountsForPurchase(
  purchase: PurchaseWithTotals,
  offer: Offer,
): DiscountDelta[] {
  const { id: purchaseId, plucode, choicesWithQuantity } = purchase;
  const item = purchase.sizeId
    ? purchase.item.sizes?.find(s => s.id === purchase.sizeId) || purchase.item
    : purchase.item;

  let discounts: DiscountDelta[] = [];
  const { targetPLUs, applyEvenIfPluIsItemOrIngredient, type, amount, limit } =
    offer;
  const offerAmountCents = dollarsToCents(amount);

  // No PLU restictions means if you keep target plus, target categories or target classes empty on PC coupon then that means we can apply the coupon to any item
  const noPLURestrictions = !targetPLUs.length;

  const offerApplicable =
    includesTargetPLUs(choicesWithQuantity, targetPLUs, plucode) ||
    noPLURestrictions;

  // Returning no discount if offer is not applicable for current purchase
  if (!offerApplicable) {
    return discounts;
  }

  // Checking whether we can apply discount to parent item
  const canApplyToParent =
    (item?.plucode && targetPLUs.includes(item.plucode)) || noPLURestrictions;

  // AMOUNT_OFF_ITEM discount calculation
  const calcAmountOff = (data: { baseMoneyPrice: number }) =>
    Math.min(offerAmountCents, data.baseMoneyPrice);

  // PERCENTAGE_OFF_ITEM_OR_INGREDIENTS discount calculation
  const calcPercentageOff = (data: { baseMoneyPrice: number }) => {
    const calculatedAmount = Math.round((amount / 100) * data.baseMoneyPrice);
    const offerLimit = limit ? limit : data.baseMoneyPrice;
    return Math.min(calculatedAmount, offerLimit);
  };

  // PERCENTAGE_OFF_ITEM_AND_INGREDIENTS discount calculation
  const calcPercentageOffTotal = (data: { moneyPrice: number }) => {
    const calculatedAmount = Math.round((amount / 100) * data.moneyPrice);
    const offerLimit = limit ? limit : data.moneyPrice;
    return Math.min(calculatedAmount, offerLimit);
  };

  // PRICE_OVERRIDE_ITEM_OR_INGREDIENT discount calculation
  const calcPriceOverride = (oldPrice: number) =>
    offerAmountCents < oldPrice ? oldPrice - offerAmountCents : 0;

  /*
  NOTES
  - Instructions, Choices, Ingredients are all same thing (just different words)
  - This config applyEvenIfPluIsItemOrIngredient tells us when to apply the discount
    CONFIG turned ON means apply discount to the selected PLUs even if they are part of another item as an ingredient/instruction or are the parent item themselves.
    CONFIG turned OFF means apply discount to the selected PLUs only if they are the parent item not when they are an ingredient/instruction of another item


  - https://www.figma.com/design/MDkfzqrTaraD9sEf1eCbNv/Coupon-Use-Cases?node-id=0-1&t=gG48OCGe3HW8TH1S-1
  - Go through the FIGMA file to understand how coupons work
  */

  switch (type) {
    case OFFER_TYPE.AMOUNT_OFF_ITEM: {
      if (canApplyToParent) {
        discounts.push({
          amount: calcAmountOff(item),
          purchaseId,
        });
      }

      if (applyEvenIfPluIsItemOrIngredient) {
        applyDiscountToChoices(
          choicesWithQuantity,
          targetPLUs,
          purchaseId,
          c => calcAmountOff(c),
          discounts,
        );
      }

      break;
    }

    case OFFER_TYPE.PERCENTAGE_OFF_ITEM_POS_ENFORCED:
    case OFFER_TYPE.PERCENTAGE_OFF_ITEM_OR_INGREDIENTS: {
      if (canApplyToParent) {
        discounts.push({
          amount: calcPercentageOff(item),
          purchaseId,
        });
      }

      if (applyEvenIfPluIsItemOrIngredient) {
        applyDiscountToChoices(
          choicesWithQuantity,
          targetPLUs,
          purchaseId,
          c => calcPercentageOff(c),
          discounts,
        );
      }

      break;
    }

    case OFFER_TYPE.PERCENTAGE_OFF_ITEM_AND_INGREDIENTS: {
      if (canApplyToParent) {
        const individualPurchase = asIndividual(purchase);
        discounts.push({
          amount: calcPercentageOffTotal(individualPurchase),
          purchaseId,
        });
      }

      if (applyEvenIfPluIsItemOrIngredient) {
        applyDiscountToChoices(
          choicesWithQuantity,
          targetPLUs,
          purchaseId,
          c =>
            calcPercentageOffTotal({
              // Modifying the money price because we want to keep the calculation with one quantity
              moneyPrice: c.moneyPrice / c.quantity,
            }),
          discounts,
        );
      }

      break;
    }

    case OFFER_TYPE.PRICE_OVERRIDE_ITEM_OR_INGREDIENT: {
      // Checking if we can apply to the parent
      if (canApplyToParent) {
        discounts.push({
          amount: Math.min(
            purchase.moneyPrice,
            calcPriceOverride(item.baseMoneyPrice),
          ),
          purchaseId,
        });
      }

      // Function to apply price overrides from choice/ingredient levels
      const applyOverridesOnIngredientLevel = (
        choices: Array<ChoiceWithQuantity | NestedChoiceWithQuantity>,
      ) => {
        for (const choice of choices) {
          if (!choice?.freeChoice && targetPLUs.includes(choice.plucode)) {
            discounts.push({
              amount: Math.min(
                purchase.moneyPrice,
                calcPriceOverride(choice.baseMoneyPrice),
              ),
              purchaseId,
            });
          }

          if (isNestedChoiceWithQuantity(choice)) {
            applyOverridesOnIngredientLevel(getChoicesWithQuantity(choice));
          }
        }
      };

      if (applyEvenIfPluIsItemOrIngredient) {
        applyOverridesOnIngredientLevel(choicesWithQuantity);
      }

      break;
    }
  }

  return discounts.filter(d => d.amount > 0);
}
